import React, { useState, useEffect } from 'react';
import { Menu, Video } from 'lucide-react';
import { supabase } from './lib/supabase';
import Header from './components/Header';
import Auth from './components/Auth';
import PracticeMode from './components/PracticeMode';
import MultiplicationGame from './components/MultiplicationGame';
import PrimeHunterGame from './components/PrimeHunterGame';
import PrimeSortingGame from './components/PrimeSortingGame';
import PrimeQuizGame from './components/PrimeQuizGame';
import Progress from './components/Progress';
import SquareMagicGame from './components/SquareMagicGame';
import SquareTargetGame from './components/SquareTargetGame';
import SquareGuessGame from './components/SquareGuessGame';
import MathBombGame from './components/MathBombGame';
import ProbabilityWheelGame from './components/ProbabilityWheelGame';
import MysteryBagGame from './components/MysteryBagGame';
import ProbabilityRaceGame from './components/ProbabilityRaceGame';
import PizzaFractionGame from './components/PizzaFractionGame';
import JuiceLabGame from './components/JuiceLabGame';
import SpaceFractionGame from './components/SpaceFractionGame';
import PotionLabGame from './components/PotionLabGame';
import MagicScaleGame from './components/MagicScaleGame';
import WeatherChallengeGame from './components/WeatherChallengeGame';
import ClassroomManagement from './components/ClassroomManagement';
import { GameMode, Operation, Language } from './types';

function App() {
  const [selectedMode, setSelectedMode] = useState<GameMode | null>(null);
  const [selectedOperation, setSelectedOperation] = useState<Operation | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [language, setLanguage] = useState<Language>('en');
  const [session, setSession] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [showPrimeVideo, setShowPrimeVideo] = useState(false);
  const [showSquareVideo, setShowSquareVideo] = useState(false);
  const [showLogoScreen, setShowLogoScreen] = useState(true);
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw sessionError;
        }

        setSession(currentSession);
        setIsAuthenticated(!!currentSession);
        
        if (currentSession?.user) {
          await loadUserRole(currentSession.user.id);
        } else {
          setIsLoading(false);
        }

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
          setSession(newSession);
          setIsAuthenticated(!!newSession);
          
          if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
            setUserRole(null);
            setAuthError(null);
            setIsAuthenticated(false);
          } else if (newSession?.user) {
            await loadUserRole(newSession.user.id);
            setIsAuthenticated(true);
          }
          
          setIsLoading(false);
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Auth initialization error:', error);
        setAuthError(error.message);
        setIsLoading(false);
        setIsAuthenticated(false);
        
        await supabase.auth.signOut();
        localStorage.removeItem('supabase.auth.token');
      }
    };    
    initializeAuth();
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
      if (session) {
        setShowLogoScreen(false);
        setShowWelcomeVideo(false);
      }
    });
  }, []);
  
  useEffect(() => {
    if (document && document.documentElement) {
      if (localStorage.getItem('theme') === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }, []);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('theme') === 'dark';
  });
  
  const toggleDarkMode = () => {
    const isDark = !isDarkMode;
    setIsDarkMode(isDark);
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };
  
  const loadUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, role')
        .eq('id', userId)
        .single();

      if (error) throw error;
      setUserRole(data.role);
      setUserName(data.first_name ?? '');
      setAuthError(null);
    } catch (error) {
      console.error('Error loading user role:', error);
      setUserRole(null);
      setAuthError('Error loading user profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleModeSelect = (mode: GameMode, operation: Operation) => {
    setSelectedMode(mode);
    setSelectedOperation(operation);
    setIsMobileMenuOpen(false);
    setShowProgress(false);
  };

  const handleReset = () => {
    setSelectedMode(null);
    setSelectedOperation(null);
    setShowProgress(false);
  };

  const handleProgressClick = () => {
    setSelectedMode(null);
    setSelectedOperation(null);
    setShowProgress(true);
  };

  const getTitle = () => {
    return language === 'en' 
      ? 'Math Practice With MathMaster: Exceed Your Math Skills'
      : 'Exercices de Mathématiques avec MathMaster: Dépassez vos Compétences';
  };

  const getDescription = () => {
    return language === 'en'
      ? 'Master multiplication, division, prime numbers, square numbers, probability and fractions with fun, interactive practice modes.'
      : 'Maîtrisez la multiplication, la division, les nombres premiers, les nombres carrés, les probabilités et les fractions avec des modes d\'exercices amusants et interactifs.';
  };

  const renderPrimeGame = () => {
    switch (selectedMode) {
      case 'sorting':
        return (
          <PrimeSortingGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      case 'detective':
        return (
          <PrimeHunterGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      case 'quiz':
        return (
          <PrimeQuizGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      default:
        return null;
    }
  };

  const renderSquareGame = () => {
    switch (selectedMode) {
      case 'magic':
        return (
          <SquareMagicGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      case 'target':
        return (
          <SquareTargetGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      case 'guess':
        return (
          <SquareGuessGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      default:
        return null;
    }
  };

  const renderProbabilityGame = () => {
    switch (selectedMode) {
      case 'wheel':
        return (
          <ProbabilityWheelGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      case 'bag':
        return (
          <MysteryBagGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      case 'race':
        return (
          <ProbabilityRaceGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      default:
        return null;
    }
  };

  const renderFractionGame = () => {
    switch (selectedMode) {
      case 'pizza':
        return (
          <PizzaFractionGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      case 'juice':
        return (
          <JuiceLabGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      case 'space':
        return (
          <SpaceFractionGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      default:
        return null;
    }
  };

  const renderMeasuresGame = () => {
    switch (selectedMode) {
      case 'potion':
        return (
          <PotionLabGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      case 'scale':
        return (
          <MagicScaleGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      case 'weather':
        return (
          <WeatherChallengeGame
            mode={selectedMode}
            onReset={handleReset}
            language={language}
          />
        );
      default:
        return null;
    }
  };

  if (!isAuthenticated && showLogoScreen) {
    return (
      <div className="relative w-full h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <img
          src="/logo.svg"
          alt="MathMaster Logo"
          className="w-[600px] h-[600px] cursor-pointer transition-transform duration-500 hover:scale-105"
          onClick={() => {
            setShowLogoScreen(false);
            setShowWelcomeVideo(true);
          }}
        />
      </div>
    );
  }
  
  if (!isAuthenticated && showWelcomeVideo) {
    return (
      <div className="relative w-full h-screen overflow-hidden bg-black">
        <video
          src={
            isDarkMode
              ? "/videos/MathMaster_logo-Dark.mp4"
              : "/videos/MathMaster_logo-Light.mp4"
          }
          autoPlay
          muted={false}
          className="absolute top-0 left-0 w-full h-full object-cover"
          onEnded={() => setShowButton(true)}
        />
        {showButton && (
          <div className="absolute inset-0 flex items-end justify-center pb-10">
            <button
              onClick={() => setShowWelcomeVideo(false)}
              className="bg-indigo-600 text-white px-6 py-3 rounded-xl text-lg shadow-lg hover:bg-indigo-700 transition-all animate-fade-in"
            >
              Commencer
            </button>
          </div>
        )}
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className={isDarkMode ? 'dark' : ''}>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
          <Header 
            language={language} 
            onLanguageChange={setLanguage}
            onProgressClick={handleProgressClick}
            showProgressButton={false}
            isDarkMode={isDarkMode}
            onToggleDarkMode={toggleDarkMode}
            userRole={userRole}
            userName={userName}
          />
          <main className="flex-grow flex items-center justify-center">
            <div className="text-slate-600 dark:text-slate-300 text-sm">
              {language === 'en' ? 'Loading...' : 'Chargement...'}
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className={isDarkMode ? 'dark' : ''}>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
          <Header 
            language={language} 
            onLanguageChange={setLanguage}
            onProgressClick={handleProgressClick}
            showProgressButton={false}
            isDarkMode={isDarkMode}
            onToggleDarkMode={toggleDarkMode}
            userRole={userRole}
            userName={userName}
          />
          <main className="flex-grow flex items-center justify-center px-4 py-6">
            <div className="text-center">
              <p className="text-red-600 mb-4">
                {language === 'en' 
                  ? 'There was an error with your session. Please sign in again.' 
                  : 'Il y a eu une erreur avec votre session. Veuillez vous reconnecter.'}
              </p>
              <Auth language={language} />
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className={isDarkMode ? 'dark' : ''}>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
          <Header 
            language={language} 
            onLanguageChange={setLanguage}
            onProgressClick={handleProgressClick}
            showProgressButton={false}
            isDarkMode={isDarkMode}
            onToggleDarkMode={toggleDarkMode}
            userRole={userRole}
            userName={userName}
          />
          <main className="flex-grow flex items-center justify-center px-4 py-6">
            <Auth language={language} />
          </main>
        </div>
      </div>
    );
  }

  if (userRole === null) {
    return (
      <div className={isDarkMode ? 'dark' : ''}>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
          <Header 
            language={language} 
            onLanguageChange={setLanguage}
            onProgressClick={handleProgressClick}
            showProgressButton={false}
            isDarkMode={isDarkMode}
            onToggleDarkMode={toggleDarkMode}
            userRole={userRole}
            userName={userName}
          />
          <main className="flex-grow flex items-center justify-center">
            <div className="text-slate-600 dark:text-slate-300 text-sm">
              {language === 'en' ? 'Loading profile...' : 'Chargement du profil...'}
            </div>
          </main>
        </div>
      </div>
    );
  }
  
  return (
    <div className={isDarkMode ? 'dark' : ''}>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
        <Header 
          language={language} 
          onLanguageChange={setLanguage}
          onProgressClick={handleProgressClick}
          showProgressButton={isAuthenticated && userRole === 'student'}
          isDarkMode={isDarkMode}
          onToggleDarkMode={toggleDarkMode}
          userRole={userRole}
          userName={userName}
        />
      
        <main className="flex-grow flex flex-col items-center px-4 py-6 sm:py-10">
          {showProgress ? (
            <Progress language={language} />
          ) : userRole === 'teacher' ? (
            <ClassroomManagement language={language} />
          ) : selectedMode && selectedOperation ? (
            selectedOperation === 'prime' ? (
              renderPrimeGame()
            ) : selectedOperation === 'square' ? (
              renderSquareGame()
            ) : selectedOperation === 'probability' ? (
              renderProbabilityGame()
            ) : selectedOperation === 'fractions' ? (
              renderFractionGame()
            ) : selectedOperation === 'measures' ? (
              renderMeasuresGame()
            ) : selectedOperation === 'bomb' ? (
              <MathBombGame 
                mode={selectedMode}
                onReset={handleReset}
                language={language}
              />
            ) : (
              <MultiplicationGame 
                mode={selectedMode} 
                operation={selectedOperation}
                onReset={handleReset}
                language={language}
              />
            )
          ) : (
            <>
              <div className="w-full max-w-4xl mb-8 text-center">
                <h1 className="text-3xl sm:text-4xl font-bold text-indigo-700 dark:text-slate-100 mb-3">
                  {getTitle()}
                </h1>
                <p className="text-lg text-slate-600 dark:text-slate-200 max-w-2xl mx-auto">
                  {getDescription()}
                </p>
              </div>

              <div className="hidden sm:block w-full max-w-4xl space-y-12">
                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-4">
                    {language === 'en' ? 'Multiplication Tables' : 'Tables de Multiplication'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <PracticeMode
                      title={language === 'en' ? 'Table Master' : 'Maître des Tables'}
                      description={language === 'en' 
                        ? 'Choose and practice any multiplication table at your own pace'
                        : 'Choisissez et pratiquez n\'importe quelle table de multiplication à votre rythme'}
                      icon="ListOrdered"
                      onClick={() => handleModeSelect('sequential', 'multiplication')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Random' : 'Aléatoire'}
                      description={language === 'en'
                        ? 'Random multiplication questions from all tables'
                        : 'Questions aléatoires de toutes les tables'}
                      icon="Shuffle"
                      onClick={() => handleModeSelect('random', 'multiplication')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Timed Challenge' : 'Contre la Montre'}
                      description={language === 'en'
                        ? 'Answer as many questions as you can in 60 seconds'
                        : 'Répondez à un maximum de questions en 60 secondes'}
                      icon="Timer"
                      onClick={() => handleModeSelect('timed', 'multiplication')}
                    />
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-4">
                    {language === 'en' ? 'Division Tables' : 'Tables de Division'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <PracticeMode
                      title={language === 'en' ? 'Table Master' : 'Maître des Tables'}
                      description={language === 'en'
                        ? 'Choose and practice any division table at your own pace'
                        : 'Choisissez et pratiquez n\'importe quelle table de division à votre rythme'}
                      icon="ListOrdered"
                      onClick={() => handleModeSelect('sequential', 'division')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Random' : 'Aléatoire'}
                      description={language === 'en'
                        ? 'Random division questions from all tables'
                        : 'Questions de division aléatoires'}
                      icon="Shuffle"
                      onClick={() => handleModeSelect('random', 'division')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Timed Challenge' : 'Contre la Montre'}
                      description={language === 'en'
                        ? 'Answer as many questions as you can in 60 seconds'
                        : 'Répondez à un maximum de questions en 60 secondes'}
                      icon="Timer"
                      onClick={() => handleModeSelect('timed', 'division')}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center mb-4 gap-2">
                    <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-200">
                      {language === 'en' ? 'Prime Numbers' : 'Nombres Premiers'}
                    </h2>
                    <button
                      onClick={() => setShowPrimeVideo(true)}
                      className="text-indigo-600 hover:text-indigo-800"
                      title={language === 'en' ? 'Watch video' : 'Voir la vidéo'}
                    >
                      <Video className="h-7 w-7" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <PracticeMode
                      title={language === 'en' ? 'Sorting Game' : 'Jeu de Tri'}
                      description={language === 'en'
                        ? 'Sort numbers into "Prime" and "Not Prime" categories'
                        : 'Triez les nombres en catégories "Premier" et "Pas Premier"'}
                      icon="MoveHorizontal"
                      onClick={() => handleModeSelect('sorting', 'prime')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Prime Hunter' : 'Chasse aux Nombres'}
                      description={language === 'en'
                        ? 'Find all prime numbers in the grid'
                        : 'Trouvez tous les nombres premiers dans la grille'}
                      icon="Target"
                      onClick={() => handleModeSelect('detective', 'prime')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Prime Quiz' : 'Quiz des Nombres Premiers'}
                      description={language === 'en'
                        ? 'Test your knowledge with quick prime number challenges'
                        : 'Testez vos connaissances avec des défis rapides'}
                      icon="BrainCircuit"
                      onClick={() => handleModeSelect('quiz', 'prime')}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center mb-4 gap-2">
                    <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-200">
                      {language === 'en' ? 'Square Numbers' : 'Nombres Carrés'}
                    </h2>
                    <button
                      onClick={() => setShowSquareVideo(true)}
                      className="text-indigo-600 hover:text-indigo-800"
                      title={language === 'en' ? 'Watch video' : 'Voir la vidéo'}
                    >
                      <Video className="h-7 w-7" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <PracticeMode
                      title={language === 'en' ? 'Magic Square' : 'Carré Magique'}
                      description={language === 'en'
                        ? 'Complete the grid with missing square numbers'
                        : 'Complétez la grille avec les nombres carrés manquants'}
                      icon="Grid"
                      onClick={() => handleModeSelect('magic', 'square')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Square Target' : 'Cible Carrée'}
                      description={language === 'en'
                        ? 'Click on perfect square numbers as they appear'
                        : 'Cliquez sur les nombres carrés parfaits qui apparaissent'}
                      icon="Target"
                      onClick={() => handleModeSelect('target', 'square')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Square Root' : 'Racine Carrée'}
                      description={language === 'en'
                        ? 'Find the number that was squared'
                        : 'Trouvez le nombre qui a été élevé au carré'}
                      icon="Brain"
                      onClick={() => handleModeSelect('guess', 'square')}
                    />
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-4">
                    {language === 'en' ? 'Probability & Chance' : 'Probabilités et Hasard'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <PracticeMode
                      title={language === 'en' ? 'Probability Wheel' : 'Roue des Probabilités'}
                      description={language === 'en'
                        ? 'Spin the wheel and learn about probability with interactive questions'
                        : 'Faites tourner la roue et apprenez les probabilités avec des questions interactives'}
                      icon="Target"
                      onClick={() => handleModeSelect('wheel', 'probability')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Mystery Bag' : 'Sac Mystérieux'}
                      description={language === 'en'
                        ? 'Predict and simulate drawing colored balls from a mystery bag'
                        : 'Prédisez et simulez le tirage de billes colorées d\'un sac mystérieux'}
                      icon="Brain"
                      onClick={() => handleModeSelect('bag', 'probability')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Probability Race' : 'Course Probabiliste'}
                      description={language === 'en'
                        ? 'Predict race winners based on dice rules and probability'
                        : 'Prédisez les gagnants de course basés sur les règles de dés et probabilités'}
                      icon="Trophy"
                      onClick={() => handleModeSelect('race', 'probability')}
                    />
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-4">
                    {language === 'en' ? 'Fractions' : 'Fractions'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <PracticeMode
                      title={language === 'en' ? 'Pizza Fractions' : 'Pizzéria des Fractions'}
                      description={language === 'en'
                        ? 'Learn fractions by cutting and sharing pizzas with interactive challenges'
                        : 'Apprenez les fractions en coupant et partageant des pizzas avec des défis interactifs'}
                      icon="Pizza"
                      onClick={() => handleModeSelect('pizza', 'fractions')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Juice Lab' : 'Laboratoire des Jus'}
                      description={language === 'en'
                        ? 'Mix precise amounts of juice using fractions and measurements'
                        : 'Mélangez des quantités précises de jus en utilisant les fractions et mesures'}
                      icon="Beaker"
                      onClick={() => handleModeSelect('juice', 'fractions')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Space Mission' : 'Mission Spatiale'}
                      description={language === 'en'
                        ? 'Navigate through space zones by solving fraction challenges'
                        : 'Naviguez à travers les zones spatiales en résolvant des défis de fractions'}
                      icon="Rocket"
                      onClick={() => handleModeSelect('space', 'fractions')}
                    />
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-4">
                    {language === 'en' ? 'Volume & Measurements' : 'Volume et Mesures'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <PracticeMode
                      title={language === 'en' ? 'Magic Potion Lab' : 'Laboratoire de Potions Magiques'}
                      description={language === 'en'
                        ? 'Learn volume measurements by creating magical potions with precise amounts'
                        : 'Apprenez les mesures de volume en créant des potions magiques avec des quantités précises'}
                      icon="Beaker"
                      onClick={() => handleModeSelect('potion', 'measures')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Magic Scale' : 'Balance Magique'}
                      description={language === 'en'
                        ? 'Learn about mass and weight using a magical balance with interactive challenges'
                        : 'Apprenez les masses et les poids en utilisant une balance magique avec des défis interactifs'}
                      icon="Scale"
                      onClick={() => handleModeSelect('scale', 'measures')}
                    />
                    <PracticeMode
                      title={language === 'en' ? 'Weather Challenge' : 'Défi Météo'}
                      description={language === 'en'
                        ? 'Read temperatures and make weather-appropriate choices in this interactive challenge'
                        : 'Lisez les températures et faites des choix adaptés à la météo dans ce défi interactif'}
                      icon="Thermometer"
                      onClick={() => handleModeSelect('weather', 'measures')}
                    />
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 dark:text-white mb-4">
                    {language === 'en' ? 'Math Bomb' : 'Bombe Mathématique'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                    <PracticeMode
                      title={language === 'en' ? 'Defuse the Bomb' : 'Désamorcer la Bombe'}
                      description={language === 'en'
                        ? 'Solve math puzzles to defuse the bomb before time runs out'
                        : 'Résolvez des énigmes mathématiques pour désamorcer la bombe avant la fin du compte à rebours'}
                      icon="Bomb"
                      onClick={() => handleModeSelect('defuse', 'bomb')}
                    />
                  </div>
                </div>
              </div>

              <div className="sm:hidden w-full">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="w-full flex items-center justify-between p-4 bg-white rounded-lg shadow-sm border border-slate-200"
                >
                  <span className="font-medium text-slate-800">
                    {language === 'en' ? 'Select Practice Mode' : 'Sélectionner un mode'}
                  </span>
                  <Menu className="h-5 w-5 text-slate-600" />
                </button>

                {isMobileMenuOpen && (
                  <div className="mt-2 bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
                    <div className="border-b border-slate-200 p-3 bg-slate-50">
                      <h3 className="font-semibold text-slate-700">
                        {language === 'en' ? 'Multiplication' : 'Multiplication'}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleModeSelect('sequential', 'multiplication')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Sequential' : 'Séquentiel'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Practice tables in order' : 'Exercez-vous dans l\'ordre'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('random', 'multiplication')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Random' : 'Aléatoire'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Random questions from all tables' : 'Questions aléatoires'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('timed', 'multiplication')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-200"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Timed Challenge' : 'Contre la Montre'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? '60-second challenge' : 'Défi de 60 secondes'}
                      </div>
                    </button>

                    <div className="border-b border-slate-200 p-3 bg-slate-50">
                      <h3 className="font-semibold text-slate-700">
                        {language === 'en' ? 'Division' : 'Division'}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleModeSelect('sequential', 'division')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Sequential' : 'Séquentiel'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Practice tables in order' : 'Exercez-vous dans l\'ordre'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('random', 'division')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Random' : 'Aléatoire'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Random questions from all tables' : 'Questions aléatoires'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('timed', 'division')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-200"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Timed Challenge' : 'Contre la Montre'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? '60-second challenge' : 'Défi de 60 secondes'}
                      </div>
                    </button>

                    <div className="border-b border-slate-200 p-3 bg-slate-50">
                      <h3 className="font-semibold text-slate-700">
                        {language === 'en' ? 'Prime Numbers' : 'Nombres Premiers'}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleModeSelect('sorting', 'prime')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Sorting Game' : 'Jeu de Tri'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Sort prime and non-prime numbers' : 'Triez les nombres premiers'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('detective', 'prime')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Prime Hunter' : 'Chasse aux Nombres'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Find all prime numbers in the grid' : 'Trouvez tous les nombres premiers'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('quiz', 'prime')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-200"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Prime Quiz' : 'Quiz des Nombres Premiers'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Quick prime number challenges' : 'Défis rapides sur les nombres premiers'}
                      </div>
                    </button>

                    <div className="border-b border-slate-200 p-3 bg-slate-50">
                      <h3 className="font-semibold text-slate-700">
                        {language === 'en' ? 'Square Numbers' : 'Nombres Carrés'}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleModeSelect('magic', 'square')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Magic Square' : 'Carré Magique'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Complete the grid with square numbers' : 'Complétez la grille avec les nombres carrés'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('target', 'square')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Square Target' : 'Cible Carrée'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Click on perfect square numbers' : 'Cliquez sur les nombres carrés parfaits'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('guess', 'square')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-200"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Square Root' : 'Racine Carrée'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Find the number that was squared' : 'Trouvez le nombre qui a été élevé au carré'}
                      </div>
                    </button>

                    <div className="border-b border-slate-200 p-3 bg-slate-50">
                      <h3 className="font-semibold text-slate-700">
                        {language === 'en' ? 'Probability' : 'Probabilités'}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleModeSelect('wheel', 'probability')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Probability Wheel' : 'Roue des Probabilités'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Interactive wheel with probability questions' : 'Roue interactive avec questions de probabilité'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('bag', 'probability')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Mystery Bag' : 'Sac Mystérieux'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Predict and simulate ball drawing' : 'Prédisez et simulez le tirage de billes'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('race', 'probability')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-200"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Probability Race' : 'Course Probabiliste'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Predict race winners with dice rules' : 'Prédisez les gagnants avec règles de dés'}
                      </div>
                    </button>

                    <div className="border-b border-slate-200 p-3 bg-slate-50">
                      <h3 className="font-semibold text-slate-700">
                        {language === 'en' ? 'Fractions' : 'Fractions'}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleModeSelect('pizza', 'fractions')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Pizza Fractions' : 'Pizzéria des Fractions'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Learn fractions by cutting pizzas' : 'Apprenez les fractions en coupant des pizzas'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('juice', 'fractions')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Juice Lab' : 'Laboratoire des Jus'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Mix precise amounts using fractions' : 'Mélangez des quantités précises avec les fractions'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('space', 'fractions')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Space Mission' : 'Mission Spatiale'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Navigate space with fraction challenges' : 'Naviguez dans l\'espace avec des défis de fractions'}
                      </div>
                    </button>

                    <div className="border-b border-slate-200 p-3 bg-slate-50">
                      <h3 className="font-semibold text-slate-700">
                        {language === 'en' ? 'Volume & Measurements' : 'Volume et Mesures'}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleModeSelect('potion', 'measures')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Magic Potion Lab' : 'Laboratoire de Potions Magiques'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Learn volume measurements with magical potions' : 'Apprenez les mesures de volume avec des potions magiques'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('scale', 'measures')}
                      className="w-full text-left p-4 hover:bg-slate-50 border-b border-slate-100"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Magic Scale' : 'Balance Magique'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Learn mass and weight with interactive balance' : 'Apprenez les masses et poids avec une balance interactive'}
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeSelect('weather', 'measures')}
                      className="w-full text-left p-4 hover:bg-slate-50"
                    >
                      <div className="font-medium text-slate-800">
                        {language === 'en' ? 'Weather Challenge' : 'Défi Météo'}
                      </div>
                      <div className="text-sm text-slate-500">
                        {language === 'en' ? 'Read temperatures and make weather choices' : 'Lisez les températures et faites des choix météo'}
                      </div>
                    </button>
                  </div>
                )}
              
              </div>
            </>
          )}
        </main>

        <footer className="bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 py-4">
          <div className="container mx-auto px-4 flex justify-between items-center text-sm">
            <div className="text-slate-500 dark:text-slate-400">
              © {new Date().getFullYear()} MathMaster - Version 1.4 (BETA)
            </div>
            <button
              onClick={() => setShowAboutModal(true)}
              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
            >
              {language === 'en' ? 'About Us' : 'À propos de nous'}
            </button>
          </div>
        </footer>

        {showPrimeVideo && (
          <div className="fixed inset-0 bg-black bg-opacity-70 z-50  flex items-center justify-center">
            <div className="bg-white dark:bg-slate-900 p-4 rounded-lg max-w-3xl w-full shadow-lg relative">
              <button
                onClick={() => setShowPrimeVideo(false)}
                className="absolute top-2 right-2 text-slate-600 hover:text-red-500"
              >
                ✕
              </button>
              <video controls className="w-full rounded-md mt-6">
                <source
                  src={language === 'fr'
                    ? '/videos/Nombres_Premiers.mp4'
                    : '/videos/Prime_Numbers.mp4'}
                  type="video/mp4"
                />
                {language === 'en'
                  ? 'Your browser does not support the video tag.'
                  : 'Votre navigateur ne supporte pas la vidéo.'}
              </video>
            </div>
          </div>
        )}
        {showSquareVideo && (
          <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center">
            <div className="bg-white dark:bg-slate-900 p-4 rounded-lg max-w-3xl w-full shadow-lg relative">
              <button
                onClick={() => setShowSquareVideo(false)}
                className="absolute top-2 right-2 text-slate-600 hover:text-red-500"
              >
                ✕
              </button>
              <video controls className="w-full rounded-md mt-6">
                <source
                  src={language === 'fr'
                    ? '/videos/Nombres_Carres.mp4'
                    : '/videos/Square_Numbers.mp4'}
                  type="video/mp4"
                />
                {language === 'en'
                  ? 'Your browser does not support the video tag.'
                  : 'Votre navigateur ne supporte pas la vidéo.'}
              </video>
            </div>
          </div>
        )}
        {showAboutModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-lg max-w-2xl w-full shadow-lg relative">
              <button
                onClick={() => setShowAboutModal(false)}
                className="absolute top-2 right-2 text-slate-600 dark:text-slate-400 hover:text-red-500"
              >
                ✕
              </button>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                {language === 'en' ? 'About MathMaster' : 'À propos de MathMaster'}
              </h2>
              <div className="prose dark:prose-invert max-w-none">
                <p className="text-slate-600 dark:text-slate-300">
                  {language === 'en' 
                    ? 'MathMaster is an innovative educational platform designed to make learning mathematics fun and engaging. Our mission is to help students develop strong mathematical foundations through interactive exercises and games.'
                    : 'MathMaster est une plateforme éducative innovante conçue pour rendre l\'apprentissage des mathématiques amusant et engageant. Notre mission est d\'aider les élèves à développer de solides bases en mathématiques grâce à des exercices et des jeux interactifs.'}
                </p>
                <p className="text-slate-600 dark:text-slate-300 mt-4">
                  {language === 'en'
                    ? 'We believe that every student has the potential to excel in mathematics when provided with the right tools and environment. Our platform combines proven educational methods with modern technology to create an effective learning experience.'
                    : 'Nous croyons que chaque élève a le potentiel d\'exceller en mathématiques lorsqu\'il dispose des bons outils et du bon environnement. Notre plateforme combine des méthodes éducatives éprouvées avec la technologie moderne pour créer une expérience d\'apprentissage efficace.'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;